import { Link, useLoaderData } from 'react-router-dom';
import { loadMethodologyBundle, type MethodologyBundle } from '../../data/loaders';
import { formatLargeInt } from '../../lib/format';
import { usePageMeta } from '../../lib/use-page-meta';
import styles from './MethodologyPage.module.css';

export async function methodologyLoader(): Promise<MethodologyBundle> {
  return loadMethodologyBundle();
}

export function MethodologyPage() {
  const { meta } = useLoaderData() as MethodologyBundle;

  usePageMeta({
    title: 'Methodology · ViBE',
    description:
      'How ViBE collects, judges, and aggregates public discussion about frontier model families. The pipeline, the evidence rules, the judge rubric, ranking, and limitations.',
  });

  return (
    <article className={styles.page}>
      <header className={`${styles.header} container-wide`}>
        <p className={styles.eyebrow}>Methodology</p>
        <h1 className={styles.headline}>
          How a tweet becomes a number.
        </h1>
        <p className={styles.standfirst}>
          A short, plain-language summary of how ViBE collects, judges, and aggregates public discussion about
          frontier model families. The full procedure — design rationale, prior comparisons, sensitivity tests,
          and limitations — is in the <Link to="/paper">working paper</Link>.
        </p>
      </header>

      <hr className={`rule-strong container-wide ${styles.rule}`} />

      <section className={`${styles.body} container-wide`}>
        <aside className={styles.toc} aria-label="Sections">
          <p className={styles.tocLabel}>On this page</p>
          <ol className={styles.tocList}>
            <li><a href="#pipeline">The pipeline</a></li>
            <li><a href="#evidence">What counts as evidence</a></li>
            <li><a href="#labels">How the judge labels posts</a></li>
            <li><a href="#ranking">How ranking works</a></li>
            <li><a href="#limitations">Limitations</a></li>
          </ol>
          <p className={styles.tocFootnote}>
            Issue {meta.week} · {formatLargeInt(meta.bootstrap_iterations)} bootstrap iterations · judge {meta.judge_models[0]}.
          </p>
        </aside>

        <div className={styles.prose}>
          <section id="pipeline">
            <p className={styles.sectionEyebrow}>01 · Pipeline</p>
            <h2>Six steps from raw post to ranked score.</h2>
            <p>
              This ViBE release is a one-week artifact. Each step is a separate Node script with a clearly named output
              artifact, so the scoring run can be re-derived from the raw fetch logs alone.
            </p>
            <ol className={styles.pipelineList}>
              <li><span className={styles.stepNum}>01</span><span><strong>Fetch.</strong> Collect public tweets mentioning any tracked family, scoped to a single observed week.</span></li>
              <li><span className={styles.stepNum}>02</span><span><strong>Normalize.</strong> Strip duplicates, dedupe quote-retweet chains, collapse variants into family identifiers, and resolve aliases.</span></li>
              <li><span className={styles.stepNum}>03</span><span><strong>Score.</strong> Each post is sent to an independent judge model with a structured rubric. The judge returns labels with explicit evidence spans and a confidence value.</span></li>
              <li><span className={styles.stepNum}>04</span><span><strong>Aggregate.</strong> Per-family component scores are computed from the labelled posts and shrunk toward the population mean to control small-sample inflation.</span></li>
              <li><span className={styles.stepNum}>05</span><span><strong>Bootstrap.</strong> Confidence intervals are computed from {formatLargeInt(meta.bootstrap_iterations)} bootstrap resamples per family.</span></li>
              <li><span className={styles.stepNum}>06</span><span><strong>Export.</strong> Results are written to a small set of JSON artifacts that the website and the paper render directly from.</span></li>
            </ol>
          </section>

          <section id="evidence">
            <p className={styles.sectionEyebrow}>02 · Evidence</p>
            <h2>What counts as evidence.</h2>
            <p>
              Only posts that the judge classifies as <em>about</em> a tracked family contribute to that family&rsquo;s score.
              Tangential mentions, listicles, and posts that name the family but say nothing about it are excluded
              before any aggregation. Within the about-family set, only high-confidence labels feed the headline score —
              medium and low-confidence labels are still recorded for transparency but contribute less weight.
            </p>
            <p>
              Each labelled post carries an <em>evidence span</em>: the exact substring of the post that the judge cited
              when assigning the label. The evidence span is a check on the judge — a label without a defensible
              evidence span is treated as a soft signal and downweighted.
            </p>
          </section>

          <section id="labels">
            <p className={styles.sectionEyebrow}>03 · Labels</p>
            <h2>How the judge labels posts.</h2>
            <p>
              Each post is sent through a structured rubric covering six dimensions: overall sentiment, vibe sentiment,
              hype framing, usage intent, perceived quality, and reliability claims. Sentiment is on a six-point scale
              from strongly negative to strongly positive, with explicit slots for <em>mixed</em> and <em>not applicable</em>.
            </p>
            <p>
              The judge does not see other posts when labelling. Each post is judged in isolation against the same
              rubric, so the same author writing two contradictory posts about the same family in the same week will
              produce two contradictory labels — by design.
            </p>
            <p>
              The current judge model is <code>{meta.judge_models[0]}</code>. The same rubric has been re-run with
              alternative judge models in audit weeks; results remain stable on the dimensions where independent
              judges agree, and unstable where they do not. Where they do not, this is a real signal about the
              underlying ambiguity, not a bug.
            </p>
          </section>

          <section id="ranking">
            <p className={styles.sectionEyebrow}>04 · Ranking</p>
            <h2>How ranking works.</h2>
            <p>
              Component scores are normalized to a 0&ndash;100 scale where 50 is neutral. The headline reception score
              is a weighted combination of overall sentiment, vibe sentiment, perceived quality, reliability,
              cost perception, and sustained usage intent. Weights are fixed by formula version
              (<code>{meta.formula_version}</code>) and recorded in the data exports so any historical week can be
              re-scored under any formula.
            </p>
            <p>
              Bootstrap confidence intervals are 95% intervals computed from {formatLargeInt(meta.bootstrap_iterations)}{' '}
              resamples of the underlying labelled posts. Two families whose intervals overlap are not statistically
              distinguishable — the leaderboard order between them this week is provisional, not declarative.
            </p>
            <p>
              A family is <em>tracked but not ranked</em> when its high-confidence sample size is below a fixed threshold.
              The threshold is intentional. A family with eight high-confidence posts in a week can land anywhere on the
              scale, and publishing a confident-looking rank for that family would be dishonest.
            </p>
          </section>

          <section id="limitations">
            <p className={styles.sectionEyebrow}>05 · Limitations</p>
            <h2>What this measures, and what it does not.</h2>
            <p>
              ViBE measures the <em>received</em> behaviour of model families — what people say after using them.
              That is a different quantity from raw capability, and the two will sometimes disagree. A family can win
              every benchmark on the leaderboard and still rank low here because the people who use it find it cold,
              over-cautious, or annoying. A family can lose on every capability test and still rank high here because
              the people who use it find it pleasant, fast, or trustworthy.
            </p>
            <p>
              ViBE is also a <em>public-discourse</em> measurement. It does not survey enterprise users; it does not
              read API logs; it does not interview anyone. It reads the part of the conversation that happens out
              loud on Twitter. Families that are heavily used inside companies but seldom discussed in public will be
              under-represented here, by definition.
            </p>
            <p>
              Finally, ViBE is a <em>one-week</em> measurement. It is sensitive to news, releases, outages, and the
              shape of the conversation in the week of collection. A single week is not a verdict. It is a reading.
            </p>
          </section>

          <section className={styles.paperLink}>
            <hr className="rule" />
            <p className={styles.paperLinkLabel}>For the full procedure</p>
            <h3>Read the working paper.</h3>
            <p className={styles.paperLinkBody}>
              Construct definitions, prior-work positioning, sensitivity analyses, judge agreement statistics, and
              the bits of methodological writing that belong in an appendix and not on a homepage.
            </p>
            <Link to="/paper" className={styles.paperLinkCta}>Open the paper →</Link>
          </section>
        </div>
      </section>
    </article>
  );
}
